import classNames from "classnames";
import { CSSProperties, memo, useCallback, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import styled from "styled-components/macro";

type Variant = "primary" | "secondary" | "inverted";

interface WrapperProps {
  size?: number;
  variant?: Variant;
  backgroundOverride?: boolean;
  $focusInset?: boolean;
}

interface IconWrapperProps {
  variant?: Variant;
  $hovered?: boolean;
}

interface StyledSVGProps {
  variant?: Variant;
  $hovered?: boolean;
  $smartfill?: boolean;
  iconsize?: number;
}

const IconButtonWrapper = styled.div<WrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.size ? props.size : 40)}px;
  height: ${(props) => (props.size ? props.size : 40)}px;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  position: relative;
  transition: width 0.25s linear, height 0.25s linear;

  &:hover,
  &:focus-visible {
    & > div {
      width: 32px;
      height: 32px;
      border-radius: 5px;
      background-color: ${(props) => {
        if (props.backgroundOverride) return "rgba(255, 255, 255, 0.15)";
        if (props.variant === "primary") return "var(--primary-cta-translucent)";
        if (props.variant === "inverted") return "var(--icon-inverted-background-hover)";
        return "var(--menu-color-hover)";
      }};
    }
  }

  &:focus-visible {
    outline: 2px solid var(--primary-cta-high-contrast);
    outline-offset: ${(props) => (props.$focusInset ? "-2px" : "2px")};
    border-radius: 8px;
  }

  &:active {
    & > div {
      height: 28px;
      width: 28px;
      background-color: ${(props) => {
        if (props.backgroundOverride) return "rgba(255, 255, 255, 0.25)";
        if (props.variant === "primary") return "var(--primary-cta-transparent-30)";
        if (props.variant === "inverted") return "var(--icon-inverted-background-active)";
        return "var(--menu-color-active)";
      }};
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease-in-out;
  border-radius: 30px;

  ${(props) =>
    props.variant === "primary" &&
    `
    background-color: transparent;
    border: 1px solid transparent;
  `}

  ${(props) =>
    props.variant === "inverted" &&
    `
    background-color: transparent;
    color: var(--icon-inverted-color);
  `}

  ${(props) =>
    props.variant === "inverted" &&
    props.$hovered &&
    `
    color: var(--icon-inverted-text-on-hover);
  `}
`;

const StyledSVG = styled(SVG)<StyledSVGProps>`
  width: ${(props) => (props.iconsize ? props.iconsize : 24)}px;
  height: ${(props) => (props.iconsize ? props.iconsize : 24)}px;
  fill: ${(props) => (props.$smartfill ? "currentColor" : "default")};
  path {
    fill: ${(props) => {
      if (props.variant === "primary") return "var(--primary-cta-high-contrast)";
      if (props.variant === "secondary") return "var(--grey-0)";
      if (props.variant === "inverted") {
        return props.$hovered
          ? "var(--icon-inverted-text-on-hover)"
          : "var(--icon-inverted-color)";
      }
      return props.$smartfill ? "var(--grey-0)" : "default";
    }};
  }
`;

interface IconButtonProps {
  src: string;
  size?: number;
  iconSize?: number;
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  className?: string;
  smartFill?: boolean;
  variant?: Variant;
  ariaLabel?: string;
  hoverTipText?: string;
  disabled?: boolean;
  disableTooltip?: boolean;
  backgroundOverride?: boolean;
  wrapperClassName?: string;
  disableHoverTipArrow?: boolean;
  style?: CSSProperties;
  triggerClassName?: string;
  tooltipDelay?: number;
  focusInset?: boolean;
}

const IconButton = ({
  src,
  size,
  iconSize,
  onClick,
  className,
  smartFill = true,
  variant = "secondary",
  ariaLabel,
  hoverTipText,
  disabled = false,
  disableTooltip = false,
  backgroundOverride = false,
  wrapperClassName,
  disableHoverTipArrow = true,
  style,
  triggerClassName = "",
  tooltipDelay = 0,
  focusInset = false,
  ...props
}: IconButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const handleMouseEnter = useCallback(() => {
    if (tooltipDelay > 0) {
      hoverTimeoutRef.current = setTimeout(() => setHovered(true), tooltipDelay);
    } else {
      setHovered(true);
    }
  }, [tooltipDelay]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHovered(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (disabled) return;
      onClickRef.current?.(e);
    },
    [disabled],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled) {
        e.preventDefault();
        e.stopPropagation();
        onClickRef.current?.(e);
      }
    },
    [disabled],
  );

  // This is a div[role=button], not a native <button>, so the browser can't apply its usual
  // "no focus ring after a pointer click" heuristic — :focus-visible would otherwise match on
  // every click. Suppressing focus on mousedown keeps the ring for keyboard (Tab) users only.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <SudoTooltip
      text={hoverTipText}
      disabled={disableTooltip || !hovered || disabled}
      hasArrow={!disableHoverTipArrow}
      triggerClassName={`max-w-[80px] ${triggerClassName}`}
      position="bottom"
    >
      <IconButtonWrapper
        size={size}
        variant={variant}
        onClick={handleClick}
        className={classNames("icon-button-wrapper", className, { disabled })}
        tabIndex={0}
        role="button"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        backgroundOverride={backgroundOverride}
        $focusInset={focusInset}
        style={style}
        {...props}
      >
        <IconWrapper
          variant={variant}
          className={classNames("icon-wrapper", wrapperClassName)}
          $hovered={hovered}
        >
          <StyledSVG
            $smartfill={smartFill}
            variant={variant}
            $hovered={hovered}
            src={src}
            iconsize={iconSize}
          />
        </IconWrapper>
      </IconButtonWrapper>
    </SudoTooltip>
  );
};

export default memo(IconButton);
